// Report Builder template model — see docs/Report-Builder-Implementation-Plan.md.
// The JSONB shape stored in data.report_template.config is owned here: the
// builder validates against these types before saving, and migration 034's
// seed was written to this shape.

export type ReportTimeframe = 'daily' | 'weekly' | 'monthly' | 'custom';

export type HeaderFieldSource = 'derived' | 'manual';

export interface HeaderField {
  /** A derived preset key (see DERIVED_FIELD_LABELS) or 'custom'. */
  key: string;
  /** Display label — editable even for presets ("Pupil" vs "Child Name"). */
  label: string;
  /**
   * 'derived' = computed from app data at render time (resolvers in
   * reportFieldResolvers.ts); 'manual' = value typed by a person, either as a
   * template default or at generation time. Derived fields never take manual
   * template values (no defaultValue) — provenance stays honest — but may be
   * editableAtGeneration, where the computed value pre-fills the input.
   */
  source: HeaderFieldSource;
  /** Manual only: value set in the Report Builder. */
  defaultValue?: string;
  /** Renders an input on the generation panel, pre-filled with defaultValue
   *  (manual) or the computed value (derived, e.g. correcting Pupil Name). */
  editableAtGeneration?: boolean;
  /** Editable only: blocks Generate until a value is present. */
  required?: boolean;
  /** In a 2-column header, this field spans both columns. */
  fullWidth?: boolean;
}

/** Stat tiles the attendance section can render, in canonical display order. */
export type AttendanceTileKey =
  | 'attendance_rate'
  | 'sessions_attended'
  | 'sessions_scheduled'
  | 'hours_attended'
  | 'hours_scheduled'
  | 'days_attended'
  | 'absences'
  | 'on_time_rate'
  | 'avg_minutes_late'
  | 'left_early';

/** Charts the attendance section can render, in canonical display order. */
export type AttendanceChartKey =
  | 'session_timeline'
  | 'weekly_trend'
  | 'hours_per_week'
  | 'hours_per_day'
  | 'punctuality'
  | 'outcome'
  | 'by_day';

/**
 * When a chart is worth printing. A single week collapses every time-series
 * chart to one data point, and the session timeline needs one row per
 * occurrence so it only stays readable over a short period. The section
 * enforces this against real data; the builder greys the option out by
 * timeframe so the choice is honest at edit time.
 */
export type AttendanceChartScope = 'any' | 'multi-week' | 'single-week';

export interface AttendanceChartMeta {
  key: AttendanceChartKey;
  label: string;
  scope: AttendanceChartScope;
}

export const ATTENDANCE_CHART_META: AttendanceChartMeta[] = [
  { key: 'session_timeline', label: 'Session timeline', scope: 'single-week' },
  { key: 'weekly_trend', label: 'Weekly attendance rate', scope: 'multi-week' },
  { key: 'hours_per_week', label: 'Hours per week', scope: 'multi-week' },
  { key: 'hours_per_day', label: 'Hours per day', scope: 'any' },
  { key: 'punctuality', label: 'Punctuality', scope: 'any' },
  { key: 'outcome', label: 'Outcomes', scope: 'any' },
  { key: 'by_day', label: 'Attendance by day', scope: 'multi-week' },
];

export const ATTENDANCE_TILE_LABELS: Record<AttendanceTileKey, string> = {
  attendance_rate: 'Attendance rate',
  sessions_attended: 'Sessions attended',
  sessions_scheduled: 'Sessions scheduled',
  hours_attended: 'Hours attended',
  hours_scheduled: 'Hours scheduled',
  days_attended: 'Days attended',
  absences: 'Absences',
  on_time_rate: 'Arrived on time',
  avg_minutes_late: 'Avg. minutes after start',
  left_early: 'Left before end',
};

/** Absent `tiles` renders these — the fixed set the section shipped with. */
export const DEFAULT_ATTENDANCE_TILES: AttendanceTileKey[] = [
  'attendance_rate', 'sessions_attended', 'sessions_scheduled',
  'hours_attended', 'days_attended', 'absences',
];

/**
 * Absent `chartKeys` renders these. Deliberately includes the two week-native
 * charts (decision 2026-09-06): a weekly report's only period-agnostic chart
 * used to be the outcome donut, which on a normal week — every session
 * attended — is a single solid circle saying nothing. Templates that never
 * named their charts therefore DO gain the timeline and punctuality; that is
 * the point. `hours_per_day` stays opt-in to keep the default page short.
 */
export const DEFAULT_ATTENDANCE_CHARTS: AttendanceChartKey[] = [
  'session_timeline', 'weekly_trend', 'hours_per_week', 'punctuality', 'outcome', 'by_day',
];

export type ReportSection =
  | { type: 'posts'; includeImages: boolean }
  | {
      type: 'attendance';
      /** Master toggles — off hides the whole tile/chart group. */
      charts: boolean;
      stats: boolean;
      /** Which tiles to render. Absent = DEFAULT_ATTENDANCE_TILES. */
      tiles?: AttendanceTileKey[];
      /** Which charts to render. Absent = DEFAULT_ATTENDANCE_CHARTS. */
      chartKeys?: AttendanceChartKey[];
    };

/**
 * Header branding. The logo itself is org-level (Admin → Organisation) — the
 * template only chooses whether to use it, so there's one place to maintain
 * branding. 'none' is for de-identified output (LA submissions, tribunal
 * bundles) where the alias/initials presets would be undone by a logo.
 */
export type HeaderLogo = 'org' | 'none';

export interface ReportTemplateConfig {
  timeframe: ReportTimeframe;
  headerColumns: 1 | 2;
  /** Absent = 'org' — pre-existing templates keep their logo. */
  headerLogo?: HeaderLogo;
  headerFields: HeaderField[];
  sections: ReportSection[];
}

export interface ReportTemplate {
  /** null = the code-defined stock template (no DB row until an org overrides it). */
  id: number | null;
  /** 'weekly-report' marks the stock template / its per-org override; null = custom. */
  slug: string | null;
  name: string;
  config: ReportTemplateConfig;
}

/** Well-known slug of the stock template's per-org override row. */
export const WEEKLY_REPORT_SLUG = 'weekly-report';

/** Derived preset keys the resolver registry understands, with builder labels. */
export const DERIVED_FIELD_LABELS: Record<string, string> = {
  report_date: 'Report Date',
  report_period: 'Report Period',
  report_type: 'Report Type',
  report_author: 'Report Author',
  setting: 'Setting',
  child_name: 'Child Name',
  child_alias: 'Child Alias',
  child_initials: 'Child Initials',
  child_ref: 'Child Reference',
  session_days: 'Session Days',
  // Phase 2 — attendance-derived (need the period's schedule + attendance).
  sessions_attended: 'Sessions Attended',
  sessions_scheduled: 'Sessions Scheduled',
  attendance_rate: 'Attendance Rate',
  hours_attended: 'Hours Attended',
  days_attended: 'Days Attended',
  absences: 'Absences',
  punctuality: 'Punctuality',
  staff: 'Staff',
};

/**
 * Derived keys that need the period's attendance/schedule data resolved.
 * `session_days` is intentionally NOT here: it upgrades to schedule-derived
 * when stats are present but falls back to post-inferred weekdays otherwise,
 * so it never forces an attendance fetch. `staff` is derived from post
 * authors, also no fetch. Callers use `templateNeedsAttendance` to decide
 * whether to load stats before rendering.
 */
export const ATTENDANCE_DERIVED_KEYS = new Set<string>([
  'sessions_attended',
  'sessions_scheduled',
  'attendance_rate',
  'hours_attended',
  'days_attended',
  'absences',
  'punctuality',
]);

/** Does this template reference attendance data (a section or a derived field)? */
export function templateNeedsAttendance(config: ReportTemplateConfig): boolean {
  if (config.sections.some(s => s.type === 'attendance')) return true;
  return config.headerFields.some(
    f => f.source === 'derived' && ATTENDANCE_DERIVED_KEYS.has(f.key),
  );
}

/**
 * The code-defined stock Weekly Report. Every org sees this even with an
 * empty report_templates table; an org row with slug 'weekly-report' shadows
 * it (copy-on-write — deleting that row is "Reset to default").
 * Deliberately minimal (decision 2026-07-15): orgs add Setting / Lead
 * Teacher / Session Days etc. via the builder if wanted.
 */
export const STOCK_WEEKLY_TEMPLATE: ReportTemplate = {
  id: null,
  slug: WEEKLY_REPORT_SLUG,
  name: 'Weekly Report',
  config: {
    timeframe: 'weekly',
    headerColumns: 1,
    headerFields: [
      { key: 'report_date', label: 'Report Date', source: 'derived' },
      { key: 'child_name', label: 'Pupil Name', source: 'derived' },
      { key: 'report_type', label: 'Report Type', source: 'derived' },
    ],
    sections: [{ type: 'posts', includeImages: true }],
  },
};

/**
 * Validate a config before saving from the builder (and defensively when
 * consuming API data). Returns human-readable problems; empty = valid.
 */
export function validateTemplateConfig(config: ReportTemplateConfig): string[] {
  const errors: string[] = [];

  if (!['daily', 'weekly', 'monthly', 'custom'].includes(config.timeframe)) {
    errors.push(`Unknown timeframe "${config.timeframe}".`);
  }
  if (config.headerColumns !== 1 && config.headerColumns !== 2) {
    errors.push('Header layout must be 1 or 2 columns.');
  }
  if (config.headerLogo !== undefined && config.headerLogo !== 'org' && config.headerLogo !== 'none') {
    errors.push(`Unknown header logo option "${config.headerLogo}".`);
  }
  if (!Array.isArray(config.headerFields)) {
    errors.push('Header fields are missing.');
  } else {
    config.headerFields.forEach((f, i) => {
      const name = f.label?.trim() || f.key || `field ${i + 1}`;
      if (!f.label?.trim()) errors.push(`Field ${i + 1} needs a label.`);
      if (f.source !== 'derived' && f.source !== 'manual') {
        errors.push(`"${name}" has an unknown source.`);
      }
      if (f.source === 'derived') {
        if (!DERIVED_FIELD_LABELS[f.key]) {
          errors.push(`"${name}" is not a known derived field.`);
        }
        // Derived fields never carry a template value (editableAtGeneration
        // is fine — the computed value pre-fills the generation input).
        if (f.defaultValue !== undefined) {
          errors.push(`"${name}" is computed automatically and can't have a template value.`);
        }
      }
      if (f.source === 'manual') {
        // A fixed (non-editable) manual field with no value can never render —
        // catch at save time, not generation time.
        if (!f.editableAtGeneration && !f.defaultValue?.trim()) {
          errors.push(`"${name}" is fixed but has no value — enter one or allow editing at generation.`);
        }
      }
      // Required blocks Generate until a value is typed — meaningless without
      // an input to type into. Applies to both sources.
      if (f.required && !f.editableAtGeneration) {
        errors.push(`"${name}" can't be required unless it's editable at generation.`);
      }
    });
  }
  if (!Array.isArray(config.sections) || config.sections.length === 0) {
    errors.push('The report needs at least one content section.');
  } else {
    const chartKeys = new Set(ATTENDANCE_CHART_META.map(c => c.key));
    for (const section of config.sections) {
      if (section.type !== 'attendance') continue;
      // An explicit-but-empty list is a mis-save, not a valid "show nothing" —
      // the master toggle is how you turn a group off.
      if (section.tiles) {
        for (const key of section.tiles) {
          if (!ATTENDANCE_TILE_LABELS[key]) errors.push(`"${key}" is not a known attendance stat.`);
        }
        if (section.stats && section.tiles.length === 0) {
          errors.push('Attendance stats are switched on but no stat is selected.');
        }
      }
      if (section.chartKeys) {
        for (const key of section.chartKeys) {
          if (!chartKeys.has(key)) errors.push(`"${key}" is not a known attendance chart.`);
        }
        if (section.charts && section.chartKeys.length === 0) {
          errors.push('Attendance charts are switched on but no chart is selected.');
        }
      }
    }
  }

  return errors;
}
